import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoidModal({ open, onClose, target, outletId, onComplete }) {
  const { token } = useAuth();
  const [reason, setReason] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVoid = async () => {
    setError('');
    
    if (!reason.trim()) {
      setError('Please provide a reason for voiding');
      return;
    }
    if (!managerPin || managerPin.length < 4) {
      setError('Please enter a valid manager PIN (at least 4 digits)');
      return;
    }

    setLoading(true);
    try {
      let endpoint = '';
      let body = { reason, manager_pin: managerPin };
      
      if (target?.type === 'item') {
        // For item void, we need ticket ID and item's order ID
        const ticketId = target.ticketId;
        const itemId = target.item.orderItemId;
        
        if (!ticketId || !itemId) {
          // Item is in cart but not yet saved to a ticket - just remove from cart
          toast.info('Item removed from cart');
          setReason('');
          setManagerPin('');
          onComplete();
          setLoading(false);
          return;
        }
        
        endpoint = `${API_URL}/api/pos/tickets/${ticketId}/items/${itemId}/void`;
      } else if (target?.type === 'ticket') {
        endpoint = `${API_URL}/api/pos/tickets/${target.ticket.id}/void`;
      } else {
        setError('Invalid void target');
        setLoading(false);
        return;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Void failed');
      }

      toast.success(`Void approved by manager`);
      setReason('');
      setManagerPin('');
      setError('');
      // Pass the void record up so the POS can print the BIR void document.
      onComplete(data?.void_log_id || null);
    } catch (err) {
      setError(err.message || 'Void failed');
      toast.error(err.message || 'Void failed');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setReason('');
    setManagerPin('');
    setError('');
    onClose();
  };

  const targetName = target?.type === 'item' ? target.item.name : target?.ticket?.ticket_number;
  const targetAmount = target?.type === 'item' 
    ? (target.item.price * target.item.quantity)
    : target?.ticket?.total;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="void-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Void {target?.type === 'item' ? 'Item' : 'Ticket'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {target?.type === 'item' 
              ? `You are about to void "${targetName}" (₱${targetAmount?.toFixed(2)}). This action requires manager approval.`
              : `You are about to void ticket ${targetName}. This cannot be undone.`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Reason for Void *</label>
            <Textarea 
              value={reason} 
              onChange={e => setReason(e.target.value)}
              placeholder="Enter reason (e.g., Customer changed mind, Wrong item entered...)" 
              className="bg-muted border-border resize-none"
              rows={3}
              data-testid="void-reason-input" 
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Manager PIN *</label>
            <Input 
              type="password" 
              inputMode="numeric"
              value={managerPin} 
              onChange={e => setManagerPin(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 4-digit manager PIN" 
              className="bg-muted border-border"
              maxLength={10} 
              data-testid="void-manager-pin-input" 
            />
            <p className="text-xs text-muted-foreground mt-1">
              Only managers and owners can approve voids
            </p>
          </div>
          
          {error && (
            <p className="text-sm text-red-600" data-testid="void-error-message">{error}</p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleVoid} 
            disabled={loading} 
            variant="destructive" 
            data-testid="confirm-void-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm Void'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
